/*
 * UwU Rewards — add the membership when the shopper adds something to their cart.
 *
 * WHY THIS EXISTS
 * The membership used to be added by snippets/cart-membership.liquid, which renders only inside
 * sections/main-cart-footer.liquid — i.e. only on /cart. /cart is optional: the cart notification's
 * "Check out" submitted the cart form straight to checkout and never rendered that page, so a
 * shopper could reach checkout with no membership line. Making the add a page-render side effect
 * was the bug; this moves it to the event that actually matters.
 *
 * HOW
 * A wrapper around fetch that fires on POST /cart/add.js and adds the membership BEFORE letting the
 * product add through. Ordering is deliberate: the theme returns freshly rendered cart sections in
 * the add response, so adding the membership first means the drawer opens already showing it, with
 * no second render and no flash.
 *
 * Shipping protection is independent of this wrapper. It is selected only after the cart is
 * complete, when the shopper chooses a protected or unprotected checkout path.
 *
 * IDEMPOTENCE — the two behaviours most likely to break here
 *   never double-add : every run re-reads /cart.js and returns early if a membership line is
 *                      already present, so a second product cannot produce a second membership,
 *                      and arriving at /cart afterwards finds _uwu_rewards_offered already set and
 *                      does nothing.
 *   decline stays declined : _uwu_rewards_offered is written as soon as the membership is offered.
 *                      Once it is set this never adds again, so adding another product cannot bring
 *                      back a membership the shopper removed.
 *
 * A campaign with no resolvable membership renders no config block at all, so the wrapper installs
 * and returns immediately — an ordinary add-to-cart is byte-identical to today.
 */
(function () {
  'use strict';

  var CONFIG_ID = 'uwu-membership-config';
  var OFFERED_KEY = '_uwu_rewards_offered';
  var ADD_URL = /\/cart\/add(\.js)?(\?|$)/;

  // Set on window by layout/theme.liquid. The literal is only a fallback if theme configuration
  // has not run yet; it is the same value that file uses.
  function protectionVariantId() {
    return String(window.__protectionVariantId || '46997990703331');
  }

  var config = null;
  var configRead = false;
  function readConfig() {
    if (configRead) return config;
    configRead = true;
    var el = document.getElementById(CONFIG_ID);
    if (!el) return (config = null);
    try {
      var parsed = JSON.parse(el.textContent || '{}');
      if (!parsed || !parsed.membership || !parsed.membership.variantId || !parsed.membership.sellingPlanId) {
        return (config = null);
      }
      config = {
        variantId: String(parsed.membership.variantId),
        sellingPlanId: String(parsed.membership.sellingPlanId),
        isMember: !!parsed.isMember,
        // The already-a-member-elsewhere guard. See snippets/uwu-other-membership.liquid.
        // A missing or malformed field degrades to "no other membership", which is the OFFERING
        // direction — so these are read defensively rather than trusted.
        hasOtherMembership: !!parsed.hasOtherMembership,
        otherProductIds: (parsed.otherMembership && Array.isArray(parsed.otherMembership.products))
          ? parsed.otherMembership.products.map(String)
          : [],
        otherVariantIds: (parsed.otherMembership && Array.isArray(parsed.otherMembership.variants))
          ? parsed.otherMembership.variants.map(String)
          : []
      };
    } catch (e) {
      config = null;
    }
    return config;
  }

  /* The theme adds to cart in three different body shapes — product-form.js sends FormData, the
   * protected-checkout fallback sends URLSearchParams, and pledgepop-scripts sends JSON. Read the
   * variant out of whichever arrived; an unreadable body is treated as "not the membership", which
   * is the safe direction because the guards below still stop a duplicate. */
  function addedVariantId(body) {
    try {
      if (!body) return null;
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var v = body.get('id');
        return v === null || v === undefined ? null : String(v);
      }
      if (typeof body === 'string') {
        var trimmed = body.trim();
        if (trimmed.charAt(0) === '{') {
          var asJson = JSON.parse(trimmed);
          return asJson && asJson.id !== undefined ? String(asJson.id) : null;
        }
        return new URLSearchParams(body).get('id');
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        return body.get('id');
      }
    } catch (e) {
      /* fall through */
    }
    return null;
  }

  function post(url, payload) {
    return fetchOriginal.call(window, url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });
  }

  async function shouldAdd(cfg, incomingVariantId) {
    lastCart = null;
    if (!cfg) return false;
    // An existing member already has one; offering a second is the thing to avoid.
    if (cfg.isMember) return false;
    // Same, for a membership bought from Simplee rather than from us: a UwU+ or VIP holder must not
    // also be sold a $3.99 Rewards subscription. Tag half — see snippets/uwu-other-membership.liquid.
    if (cfg.hasOtherMembership) return false;
    // Never react to the membership's own add, or to the protection line.
    if (incomingVariantId && incomingVariantId === cfg.variantId) return false;
    if (incomingVariantId && incomingVariantId === protectionVariantId()) return false;
    /* ⭐ The add being made RIGHT NOW is a Simplee membership. This runs before the shopper's add is
     * let through, so that item is not in the cart yet and the cart test below cannot see it. This
     * is the exact shape of #163030 — UwU+ added to an empty cart — so without this line the guard
     * misses the incident it was built for. */
    if (incomingVariantId && cfg.otherVariantIds.indexOf(String(incomingVariantId)) !== -1) return false;

    var cart;
    try {
      cart = await fetchOriginal
        .call(window, '/cart.js?ts=' + Date.now(), { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r.json(); });
    } catch (e) {
      return false; // cannot confirm the cart state, so do nothing
    }
    lastCart = cart;

    var attrs = cart.attributes || {};
    if (attrs[OFFERED_KEY]) return false;                       // declined, or already offered
    var items = Array.isArray(cart.items) ? cart.items : [];
    if (items.some(function (i) { return String(i.variant_id) === cfg.variantId; })) return false;

    /* The cart half of the already-a-member-elsewhere guard, and the reason it lives HERE rather
     * than in the config: order #163030 bought UwU+ and our Rewards membership in one checkout. At
     * the moment this ran he had no UwU+ tag — he was becoming a member in that same cart — and the
     * page had been rendered before the UwU+ line existed. A tag test cannot see that, and a
     * server-rendered cart answer would have been computed before the line was added.
     *
     * This test is against the cart re-fetched immediately above, so it sees the line that is
     * actually there at the moment we are deciding. Matching on product_id because that is what the
     * ids in snippets/uwu-other-membership.liquid are, and it survives a variant being replaced. */
    if (cfg.otherProductIds.length) {
      if (items.some(function (i) {
        return cfg.otherProductIds.indexOf(String(i.product_id)) !== -1;
      })) return false;
    }

    return true;
  }

  /* REVERSE ORDER. The guard above declines to OFFER; it cannot un-offer.
   *
   *   empty cart -> add a T-shirt -> nothing Simplee anywhere -> Rewards ADDED, _uwu_rewards_offered set
   *              -> add UwU+      -> incoming is Simplee, so we correctly do not add a second one
   *   cart = T-shirt + Rewards + UwU+ : one checkout, two memberships, two contracts.
   *
   * Same outcome as #163030 by a completely ordinary order of operations — browse, add merch, then
   * decide on the membership.
   *
   * This removes the Rewards line in that situation, and the argument for doing so is narrow: WE put
   * it there. It was auto-added; the shopper never selected it. Removing it undoes our own action
   * rather than editing a cart the shopper composed. It is not a "tidy the cart" behaviour and must
   * not be extended into one.
   *
   * It matters because the line is invisible in practice: it costs $0.00 during the trial so the
   * total never moves, and the guard above suppresses the choice table, so the Pay as Guest control
   * they would otherwise decline through is gone. The row is still rendered and still removable with
   * the cart's own trash button — but nothing on the screen tells them there is anything to remove,
   * and after the trial it bills.
   *
   * ⛔ _uwu_rewards_offered is deliberately NOT cleared. It is what makes the early return fire, and
   * leaving it set means this can never re-add. Clearing it would re-arm the auto-add and hand the
   * shopper a fresh Rewards line on their next add — the exact loop this is closing. The opt-out
   * path depends on that attribute being write-once; nothing here writes it. */
  async function reconcileStranded(cfg, incomingVariantId) {
    var cart = lastCart;

    var arriving = !!(incomingVariantId &&
      cfg.otherVariantIds.indexOf(String(incomingVariantId)) !== -1);

    if (!cart) {
      // shouldAdd returned before its fetch. Only worth a read when there is a concrete reason —
      // a Simplee membership arriving, or a tagged member — so ordinary adds cost nothing.
      if (!arriving && !cfg.hasOtherMembership) return;
      try {
        cart = await fetchOriginal
          .call(window, '/cart.js?ts=' + Date.now(), { credentials: 'same-origin', cache: 'no-store' })
          .then(function (r) { return r.json(); });
      } catch (e) {
        return; // cannot confirm the cart state, so touch nothing
      }
    }

    var items = Array.isArray(cart.items) ? cart.items : [];
    // Nothing of ours in the cart: there is nothing we put there, so there is nothing to undo.
    if (!items.some(function (i) { return String(i.variant_id) === cfg.variantId; })) return;
    // And only when a Simplee membership is actually in play: arriving now, already in the cart,
    // or held by the logged-in customer. The tag branch matters when a cart created before login
    // already carries our auto-added line; requiring a Simplee cart line would leave that duplicate.
    var present = items.some(function (i) {
      return cfg.otherProductIds.indexOf(String(i.product_id)) !== -1;
    });
    if (!arriving && !present && !cfg.hasOtherMembership) return;

    // Keyed by VARIANT ID, never a line key — the same reason assets/cart-membership.js removes it
    // this way: a stale key can remove the wrong line, a variant id cannot. This can only ever
    // remove our own membership.
    var updates = {};
    updates[cfg.variantId] = 0;
    await post('/cart/update.js', { updates: updates });
  }

  async function addMembership(cfg) {
    await post('/cart/add.js', {
      id: Number(cfg.variantId),
      quantity: 1,
      selling_plan: Number(cfg.sellingPlanId)
    });
    // Written immediately after the add, so a decline later in the session is never undone by a
    // subsequent add-to-cart. This is the same attribute /cart already uses.
    var attributes = {};
    attributes[OFFERED_KEY] = 'true';
    await post('/cart/update.js', { attributes: attributes });
  }

  var busy = false;
  var fetchOriginal = window.fetch;

  // The cart shouldAdd() last read, handed to reconcileStranded() so the common path costs no extra
  // round trip. Null whenever shouldAdd returned before reaching its fetch.
  var lastCart = null;

  window.fetch = async function uwuMembershipFetch(input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = ((init && init.method) || (input && input.method) || 'GET').toString();

      if (!busy && ADD_URL.test(url) && /^post$/i.test(method)) {
        var cfg = readConfig();
        if (cfg) {
          var incoming = addedVariantId(init && init.body);

          /* ⛔ REFUSE a direct add of the membership by someone who already holds one.
           *
           * Everything else in this file decides whether to add OURS ALONGSIDE the shopper's item;
           * none of it has ever refused a request, and shouldAdd()'s early returns all mean "do not
           * accompany this", not "do not allow this". Order #163030 is a member acquiring a second
           * membership, so the add itself has to be stopped.
           *
           * The discoverable route is closed at render time in snippets/buy-buttons.liquid, which
           * withholds the buy control from this same shopper. This is the backstop for a request
           * that did not come from that control — a page rendered before the tag existed, or a
           * scripted post.
           *
           * ⚠️ It refuses without a message. That is not the silent decline #98 ruled out: there is
           * no visible control for this shopper to have clicked, so there is no click to explain.
           * A shopper-facing sentence would need one approved line, which does not exist yet — the
           * one on the join CTA names the CART, which is untrue for a tagged member with an empty
           * one. Noted in the PR rather than invented here.
           *
           * ⚠️ Inert while signup is off: snippets/uwu-membership-boot.liquid only emits this file
           * once snippets/uwu-membership-json.liquid resolves ids, and that needs the signup flag
           * on. The render-time gate deliberately does not share that dependency. */
          if (incoming && cfg.variantId && String(incoming) === String(cfg.variantId)) {
            var directAddBlocked = cfg.isMember || cfg.hasOtherMembership;

            /* The tag answers above are fixed at page-render time. Re-read the cart so a page that
             * became stale after UwU+ / VIP entered elsewhere cannot add Rewards beside it. Also
             * refuse a second copy of Rewards already in the cart. If the cart cannot be read, fail
             * closed on this money-bearing path rather than risk creating a second contract. */
            if (!directAddBlocked) {
              try {
                var directCart = await fetchOriginal
                  .call(window, '/cart.js?ts=' + Date.now(), {
                    credentials: 'same-origin',
                    cache: 'no-store'
                  })
                  .then(function (r) { return r.json(); });
                var directItems = Array.isArray(directCart.items) ? directCart.items : [];
                directAddBlocked = directItems.some(function (i) {
                  return String(i.variant_id) === cfg.variantId ||
                    cfg.otherProductIds.indexOf(String(i.product_id)) !== -1;
                });
              } catch (e) {
                directAddBlocked = true;
              }
            }

            if (directAddBlocked) {
              return new Response(
                JSON.stringify({ status: 422, message: 'Cart Error' }),
                { status: 422, headers: { 'Content-Type': 'application/json' } }
              );
            }
          }
          busy = true; // guards the recursion: our own adds go through this same wrapper
          try {
            if (await shouldAdd(cfg, incoming)) {
              await addMembership(cfg);
            } else {
              await reconcileStranded(cfg, incoming);
            }
          } catch (e) {
            console.warn('[UwU Rewards] could not add the membership alongside this item', e);
          } finally {
            busy = false;
          }
        }
      }
    } catch (e) {
      /* never let this stop the shopper's add-to-cart */
    }
    return fetchOriginal.apply(this, arguments);
  };
})();
